import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiEye,
  FiFileText,
  FiRefreshCw,
  FiSlash,
  FiTrendingDown,
} from "react-icons/fi";

import "../SolicitacaoSaque/SolicitacaoSaque.css";
import { obterOperaçãoFinaceira as obterOperacaoFinanceiraSaque } from "./functions";

import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalAprovar } from "../../../components/modais/ModalAprovar";
import { ModalComprovante } from "../../../components/modais/ModalComprovante";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import Navbar from "../../../components/Navbar/Navbar";
import { SelectServicoFinanceiro } from "../../../components/select/SelectServicoFinanceiro";
import { handleStatus } from "../../../components/Tabelas/TabelaSolicitacaoSaque/functions";

const OPERACAO_FINANCEIRA = "Saque";
const STATUS_OPERACAO_FINANCEIRA = "EmEspera";
const STATUS_OPERACAO_LABEL = "Em espera";

export interface OperacoesFinanceiras {
  dataSolicitacao: Date;
  valor: number;
  tipo: "Aporte" | "Saque";
  status: "EmEspera" | "Efetuado" | "Rejeitado";
  urlImagemComprovante: string;
}

export interface Conta {
  servicoFinanceiroConta: string;
  operacoesFinanceiras: Array<OperacoesFinanceiras>;
}

export interface RespostaOperacaoFinanceira {
  idCredenciais: string;
  nome: string;
  conta: Conta;
}

interface OperacaoPendente extends OperacoesFinanceiras {
  idCredenciais: string;
  nome: string;
  servicoFinanceiroConta: string;
}

interface SaqueValidationItemProps {
  operacao: OperacaoPendente;
  idServicoFinanceiro: number;
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatCurrency = (value: number, idServicoFinanceiro: number) =>
  new Intl.NumberFormat(idServicoFinanceiro === 3 ? "en-US" : "pt-BR", {
    style: "currency",
    currency: idServicoFinanceiro === 3 ? "USD" : "BRL",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value: Date) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const SaqueValidationItem = ({
  operacao,
  idServicoFinanceiro,
  setModalDesconectar,
  setReload,
}: SaqueValidationItemProps) => {
  const [loadingAprovar, setLoadingAprovar] = useState<boolean>(false);
  const [modalAprovar, setModalAprovar] = useState<boolean>(false);
  const [erroAprovar, setErroAprovar] = useState<boolean>(false);
  const [comprovanteModal, setComprovanteModal] = useState<boolean>(false);
  const temComprovante = Boolean(operacao.urlImagemComprovante);

  const aprovarOperacao = () => {
    setErroAprovar(false);
    setModalAprovar(true);
  };

  const rejeitarOperacao = () => {
    setErroAprovar(false);
    handleStatus(
      operacao.idCredenciais,
      operacao.dataSolicitacao,
      "Rejeitado",
      operacao.valor,
      operacao.tipo,
      idServicoFinanceiro,
      setLoadingAprovar,
      setErroAprovar,
      setReload,
      setModalDesconectar
    );
  };

  return (
    <article className="solicitacao-saque-item">
      <div className="solicitacao-saque-receipt">
        <button
          type="button"
          onClick={() => setComprovanteModal(true)}
          disabled={!temComprovante}
          aria-label={
            temComprovante
              ? `Ver comprovante de ${operacao.nome}`
              : `Comprovante indisponível para ${operacao.nome}`
          }
        >
          {temComprovante ? <FiEye /> : <FiFileText />}
        </button>
        <span>{temComprovante ? "Comprovante" : "Sem anexo"}</span>
      </div>

      <div className="solicitacao-saque-client">
        <strong>{operacao.nome}</strong>
        <span>{operacao.servicoFinanceiroConta || "Serviço financeiro"}</span>
      </div>

      <div className="solicitacao-saque-date">
        <span>Solicitado em</span>
        <strong>{formatDate(operacao.dataSolicitacao)}</strong>
      </div>

      <div className="solicitacao-saque-value">
        <span>Valor</span>
        <strong>{formatCurrency(operacao.valor, idServicoFinanceiro)}</strong>
      </div>

      <div className="solicitacao-saque-status">
        <span>
          <FiClock />
          Aguardando
        </span>
      </div>

      <div className="solicitacao-saque-actions">
        {loadingAprovar ? (
          <LoadingButton />
        ) : (
          <>
            <button type="button" className="approve" onClick={aprovarOperacao}>
              <FiCheckCircle />
              Aprovar
            </button>
            <button type="button" className="reject" onClick={rejeitarOperacao}>
              <FiSlash />
              Reprovar
            </button>
          </>
        )}
        {erroAprovar && (
          <small>Não foi possível atualizar a operação. Tente novamente.</small>
        )}
      </div>

      {modalAprovar && (
        <ModalAprovar
          modal={modalAprovar}
          IdServicoFinanceiro={idServicoFinanceiro}
          setModal={setModalAprovar}
          setReload={setReload}
          setErroAprovar={setErroAprovar}
          dataSolicitacao={operacao.dataSolicitacao}
          idCredenciaisCliente={operacao.idCredenciais}
          status="Efetuado"
          tipo={operacao.tipo}
          valor={operacao.valor}
          setModalDesconectar={setModalDesconectar}
        />
      )}

      {comprovanteModal && (
        <ModalComprovante
          modal={comprovanteModal}
          setModal={setComprovanteModal}
          comprovanteBase64={operacao.urlImagemComprovante || ""}
        />
      )}
    </article>
  );
};

const SolicitacaoSaque = () => {
  const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
  const [data, setData] = useState<Array<RespostaOperacaoFinanceira>>([]);
  const [idServicoFinanceiro, setIdServicoFinanceiro] = useState<number>(1);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [erroPage, setErroPage] = useState<boolean>(false);
  const [reloadPage, setReloadPage] = useState<boolean>(false);

  useEffect(() => {
    setLoadingPage(true);
    obterOperacaoFinanceiraSaque(
      idServicoFinanceiro,
      OPERACAO_FINANCEIRA,
      STATUS_OPERACAO_FINANCEIRA,
      setData,
      setLoadingPage,
      setErroPage,
      setModalDesconectar
    );
  }, [reloadPage, idServicoFinanceiro]);

  const operacoesPendentes = useMemo(
    () =>
      data.flatMap((cliente) =>
        cliente.conta.operacoesFinanceiras.map((operacao) => ({
          ...operacao,
          idCredenciais: cliente.idCredenciais,
          nome: cliente.nome,
          servicoFinanceiroConta: cliente.conta.servicoFinanceiroConta,
        }))
      ),
    [data]
  );

  const totalPendente = useMemo(
    () =>
      operacoesPendentes.reduce(
        (total, operacao) => total + Number(operacao.valor || 0),
        0
      ),
    [operacoesPendentes]
  );

  const totalComprovantes = operacoesPendentes.filter(
    (operacao) => operacao.urlImagemComprovante
  ).length;

  const serviceLabel =
    idServicoFinanceiro === 3
      ? "Private"
      : idServicoFinanceiro === 2
        ? "Custódia"
        : "Assessoria";

  return (
    <>
      <Navbar />
      <main className="solicitacao-saque-page">
        <section className="solicitacao-saque-hero">
          <div>
            <span>Validação financeira</span>
            <h1>Saques pendentes</h1>
            <p>
              Analise solicitações de retirada, valide anexos e mantenha o fluxo
              de liquidez com rastreabilidade operacional.
            </p>
          </div>

          <div className="solicitacao-saque-filter">
            <label>
              <span>Serviço financeiro</span>
              <SelectServicoFinanceiro
                className="solicitacao-saque-select"
                setServico={setIdServicoFinanceiro}
              />
            </label>
            <button
              type="button"
              onClick={() => setReloadPage((value) => !value)}
              disabled={loadingPage}
            >
              <FiRefreshCw />
              Atualizar
            </button>
          </div>
        </section>

        <section className="solicitacao-saque-summary" aria-label="Resumo de saques pendentes">
          <article>
            <FiTrendingDown />
            <span>Fila pendente</span>
            <strong>{operacoesPendentes.length}</strong>
          </article>
          <article>
            <FiDollarSign />
            <span>Valor em análise</span>
            <strong>{formatCurrency(totalPendente, idServicoFinanceiro)}</strong>
          </article>
          <article>
            <FiFileText />
            <span>Com comprovante</span>
            <strong>{totalComprovantes}</strong>
          </article>
          <article>
            <FiClock />
            <span>Serviço ativo</span>
            <strong>{serviceLabel}</strong>
          </article>
        </section>

        <section className="solicitacao-saque-panel">
          <div className="solicitacao-saque-panel-header">
            <div>
              <span>Aguardando validação</span>
              <h2>Solicitações de saque</h2>
            </div>
            <strong>{STATUS_OPERACAO_LABEL}</strong>
          </div>

          {erroPage ? (
            <div className="solicitacao-saque-state error">
              <FiAlertCircle />
              <strong>Não foi possível carregar as solicitações.</strong>
              <button type="button" onClick={() => setReloadPage((value) => !value)}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : loadingPage ? (
            <div className="solicitacao-saque-state">
              <LoadingButton />
            </div>
          ) : operacoesPendentes.length ? (
            <div className="solicitacao-saque-list">
              <div className="solicitacao-saque-list-head" aria-hidden="true">
                <span>Comprovante</span>
                <span>Cliente</span>
                <span>Data</span>
                <span>Valor</span>
                <span>Status</span>
                <span>Ações</span>
              </div>
              {operacoesPendentes.map((operacao) => (
                <SaqueValidationItem
                  key={`${operacao.idCredenciais}-${operacao.dataSolicitacao}-${operacao.valor}`}
                  operacao={operacao}
                  idServicoFinanceiro={idServicoFinanceiro}
                  setModalDesconectar={setModalDesconectar}
                  setReload={setReloadPage}
                />
              ))}
            </div>
          ) : (
            <div className="solicitacao-saque-state">
              <FiCheckCircle />
              <strong>Nenhum saque pendente no momento.</strong>
              <p>A fila de validação está limpa para o serviço selecionado.</p>
            </div>
          )}
        </section>
      </main>

      {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
      <Footer />
    </>
  );
};

export default SolicitacaoSaque;
